import { apiGet, apiPost, apiPatch, apiDelete, http } from "@/services/http";
import { endpoints } from "./endpoints";
import type { ApiEnvelope } from "@/types/api";
import { ApiError } from "@/services/http/errors";
import type {
  ChatMessage,
  ChatUser,
  Conversation,
  TimelineItem,
} from "@/features/messages/data/types";

type ListResponse<T> =
  | (ApiEnvelope<T[]> & { count?: number; success?: boolean })
  | {
      count?: number;
      next?: string | null;
      previous?: string | null;
      data: T[];
      message?: string;
      success?: boolean;
    };

function unwrapList<T>(response: ListResponse<T>, fallbackMsg: string): T[] {
  const rows = Array.isArray(response?.data) ? response.data : null;
  if (!rows) throw new ApiError(0, response, fallbackMsg);
  return rows;
}

export async function fetchChatConversations(params?: { q?: string; type?: string }) {
  const query = new URLSearchParams();
  if (params?.q?.trim()) query.set("q", params.q.trim());
  if (params?.type) query.set("type", params.type);
  query.set("size", "200");
  const qs = query.toString();
  const url = `${endpoints.chatConversations}${qs ? `?${qs}` : ""}`;
  const response = await apiGet<ListResponse<Conversation>>(url);
  return unwrapList(response, "Could not load conversations.");
}

export async function fetchChatConversation(id: string) {
  const response = await apiGet<ApiEnvelope<Conversation>>(
    `${endpoints.chatConversations}${id}/`,
  );
  if (!response?.data) {
    throw new ApiError(0, response, "Could not load conversation.");
  }
  return response.data;
}

export async function createChatConversation(payload: {
  type: "dm" | "group";
  peer_user_id?: number;
  title?: string;
  member_ids?: number[];
}) {
  const response = await apiPost<ApiEnvelope<Conversation>, typeof payload>(
    endpoints.chatConversations,
    payload,
  );
  if (!response?.data) {
    throw new ApiError(0, response, "Could not create conversation.");
  }
  return response.data;
}

export async function fetchChatDirectory(q?: string) {
  const query = new URLSearchParams();
  if (q?.trim()) query.set("q", q.trim());
  const qs = query.toString();
  const url = `${endpoints.chatUsers}${qs ? `?${qs}` : ""}`;
  const response = await apiGet<ListResponse<ChatUser>>(url);
  return unwrapList(response, "Could not load users.");
}

export async function fetchChatTimeline(conversationId: string) {
  const response = await apiGet<
    ApiEnvelope<TimelineItem[]> & { dial_number?: string; success?: boolean }
  >(`${endpoints.chatConversations}${conversationId}/timeline/`);
  if (!Array.isArray(response?.data)) {
    throw new ApiError(0, response, "Could not load timeline.");
  }
  return { items: response.data, dial_number: response.dial_number || "" };
}

export async function fetchChatMessages(conversationId: string) {
  const response = await apiGet<ListResponse<ChatMessage>>(
    `${endpoints.chatConversations}${conversationId}/messages/?size=100`,
  );
  return unwrapList(response, "Could not load messages.");
}

export async function searchChatMessages(conversationId: string, q: string) {
  const query = new URLSearchParams({ q });
  const response = await apiGet<ListResponse<ChatMessage>>(
    `${endpoints.chatConversations}${conversationId}/search/?${query}`,
  );
  return unwrapList(response, "Could not search messages.");
}

export async function sendChatMessage(
  conversationId: string,
  payload: {
    body?: string;
    client_id?: string;
    file?: File;
    reply_to?: string | null;
    forwarded?: boolean;
    forwarded_from_name?: string;
    forwarded_from_message_id?: string;
  },
) {
  if (payload.file) {
    const form = new FormData();
    if (payload.body) form.append("body", payload.body);
    if (payload.client_id) form.append("client_id", payload.client_id);
    if (payload.reply_to) form.append("reply_to", payload.reply_to);
    if (payload.forwarded) form.append("forwarded", "true");
    if (payload.forwarded_from_name) {
      form.append("forwarded_from_name", payload.forwarded_from_name);
    }
    if (payload.forwarded_from_message_id) {
      form.append("forwarded_from_message_id", payload.forwarded_from_message_id);
    }
    form.append("attachment", payload.file);
    const { data } = await http.post<ApiEnvelope<ChatMessage>>(
      `${endpoints.chatConversations}${conversationId}/messages/`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    if (!data?.data) throw new ApiError(0, data, "Could not send message.");
    return data.data;
  }

  const response = await apiPost<
    ApiEnvelope<ChatMessage>,
    {
      body: string;
      client_id?: string;
      reply_to?: string | null;
      forwarded?: boolean;
      forwarded_from_name?: string;
      forwarded_from_message_id?: string;
    }
  >(`${endpoints.chatConversations}${conversationId}/messages/`, {
    body: payload.body || "",
    client_id: payload.client_id,
    reply_to: payload.reply_to || undefined,
    forwarded: payload.forwarded || undefined,
    forwarded_from_name: payload.forwarded_from_name,
    forwarded_from_message_id: payload.forwarded_from_message_id,
  });
  if (!response?.data) {
    throw new ApiError(0, response, "Could not send message.");
  }
  return response.data;
}

export async function markChatRead(conversationId: string) {
  const response = await apiPost<ApiEnvelope<{ conversation_id: string }>>(
    `${endpoints.chatConversations}${conversationId}/read/`,
  );
  return response?.data;
}

export async function sendChatTyping(
  conversationId: string,
  isTyping = true,
) {
  await apiPost(`${endpoints.chatConversations}${conversationId}/typing/`, {
    is_typing: isTyping,
  });
}

export async function chatMessageAction(
  conversationId: string,
  payload: {
    message_id: string;
    action: "delete_me" | "delete_everyone" | "delivered";
  },
) {
  const response = await apiPost<
    ApiEnvelope<ChatMessage | { message_id: string; scope?: string; status?: string }>,
    typeof payload
  >(`${endpoints.chatConversations}${conversationId}/message-actions/`, payload);
  return response?.data;
}

export async function forwardChatMessage(payload: {
  targetConversationId: string;
  body: string;
  forwarded_from_name?: string;
  forwarded_from_message_id?: string;
}) {
  return sendChatMessage(payload.targetConversationId, {
    body: payload.body,
    forwarded: true,
    forwarded_from_name: payload.forwarded_from_name,
    forwarded_from_message_id: payload.forwarded_from_message_id,
    client_id: `fwd-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  });
}

export async function updateChatConversation(
  conversationId: string,
  payload: { title?: string },
) {
  const response = await apiPatch<ApiEnvelope<Conversation>, typeof payload>(
    `${endpoints.chatConversations}${conversationId}/`,
    payload,
  );
  if (!response?.data) {
    throw new ApiError(0, response, "Could not update conversation.");
  }
  return response.data;
}

export async function leaveChatConversation(conversationId: string) {
  await apiDelete(`${endpoints.chatConversations}${conversationId}/`);
}
